import { unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './command-group.scss?inline'

/**
 * `dap-ds-command-group`
 * @summary A command group is a group of command items.
 * @element dap-ds-command-group
 * @title - Command group
 * @group command
 *
 * @slot - The default slot for the command group.
 *
 * @csspart base - The base part of the command group.
 * @csspart label - The label part of the command group.
 * @csspart items - The items part of the command group.
 *
 * @cssproperty --dds-command-group-gap - The gap between command group elements. (default: var(--dds-spacing-100))
 * @cssproperty --dds-command-group-margin-bottom - The bottom margin of the command group. (default: var(--dds-spacing-200))
 * @cssproperty --dds-command-group-border-radius - The border radius of the command group. (default: var(--dds-radius-small))
 * @cssproperty --dds-command-group-label-font-weight - The font weight of the command group label. (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-command-group-items-gap - The gap between command items. (default: var(--dds-spacing-100))
 */
@customElement('dap-ds-command-group')
export default class DapDSCommandGroup extends DdsElement {
  static readonly styles = unsafeCSS(styles)

  /**
   * The label of the command group.
   */
  @property({ type: String })
  label?: string

  /**
   * Whether the command group is exclusive, only one item can be selected at a time.
   */
  @property({ type: Boolean })
  exclusive = false

  @state()
  private _selectedItem: HTMLElement | null = null

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener(
      'dds-command-item-click',
      this._handleItemClick as EventListener,
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(
      'dds-command-item-click',
      this._handleItemClick as EventListener,
    )
  }

  private _handleItemClick(event: Event) {
    if (!this.exclusive) return

    const customEvent = event as CustomEvent
    const clickedItem = customEvent.target as HTMLElement
    if (clickedItem === this._selectedItem) return

    // Deselect previously selected item
    if (this._selectedItem) {
      this._selectedItem.removeAttribute('selected')
    }

    // Select new item
    this._selectedItem = clickedItem
    this._selectedItem.setAttribute('selected', '')
  }

  render() {
    return html`
      <div class="command-group" part="base">
        ${this.label
          ? html`<div class="command-group__label" part="label">
              ${this.label}
            </div>`
          : ''}
        <div class="command-group__items" part="items">
          <slot></slot>
        </div>
      </div>
    `
  }
}
